import { useEffect, useRef, type KeyboardEvent } from "react";
import { RotateCcw } from "lucide-react";

import type { CaixaTransactionRow } from "@/features/caixas/types";

interface CaixasUndoConfirmDialogProps {
  transaction: CaixaTransactionRow;
  onCancel: () => void;
  onConfirm: () => void;
  isPending?: boolean;
}

export function CaixasUndoConfirmDialog({
  transaction,
  onCancel,
  onConfirm,
  isPending = false,
}: CaixasUndoConfirmDialogProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  function handleCancel() {
    if (!isPending) onCancel();
  }

  function handleConfirm() {
    if (!isPending) onConfirm();
  }

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    panelRef.current
      ?.querySelector<HTMLElement>("button:not(:disabled), [href], input")
      ?.focus();

    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      handleCancel();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        "button:not(:disabled), [href], input, textarea, select, [tabindex]:not([tabindex='-1'])",
      ) ?? [],
    );

    if (focusable.length === 0) {
      event.preventDefault();
      panelRef.current?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className="caixas-filter-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="caixas-undo-dialog-title"
      onClick={handleCancel}
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 240,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        background: "rgba(0,0,0,0.46)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        ref={panelRef}
        className="caixas-filter-panel"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(100%, 420px)",
          borderRadius: "1rem",
          border: "1px solid var(--login-border)",
          background: "var(--login-surface)",
          boxShadow: "0 24px 80px rgba(15, 23, 42, 0.16)",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: "2.35rem",
              height: "2.35rem",
              borderRadius: "0.8rem",
              background: "color-mix(in srgb, #f59e0b 12%, transparent)",
              color: "#b45309",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <RotateCcw size={17} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h2
              id="caixas-undo-dialog-title"
              style={{
                margin: 0,
                color: "var(--login-text)",
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: "1.35rem",
                fontWeight: 700,
                letterSpacing: "0.03em",
              }}
            >
              Desfazer movimentação
            </h2>
            <p
              style={{
                margin: "0.45rem 0 0",
                color: "var(--login-muted)",
                fontSize: "0.92rem",
                lineHeight: 1.45,
              }}
            >
              Essa ação cria um desfazimento e mantém o lançamento original no
              histórico.
            </p>
            <p
              style={{
                margin: "0.8rem 0 0",
                color: "var(--login-text)",
                fontSize: "0.9rem",
              }}
            >
              {transaction.actor_label}
            </p>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "0.75rem",
            marginTop: "1.25rem",
          }}
        >
          <button
            type="button"
            className="caixas-pill-button caixas-pill-button-secondary"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="caixas-pill-button caixas-pill-button-primary"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? "Desfazendo..." : "Desfazer"}
          </button>
        </div>
      </div>
    </div>
  );
}
